import { useMemo, useState, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Empty, Modal, Select, Typography, message } from "antd";
import dayjs from "dayjs";

import { HabitStatus } from "@fitness-app/data-models/entities/Habit";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";

type TemplateOptionRow = {
  id: string;
  name: string;
};

type Props = {
  open: boolean;
  traineeId: string;
  userId: string | null;
  assignedTemplateIds: string[];
  onClose: () => void;
};

const AssignHabitModal: FunctionComponent<Props> = ({ open, traineeId, userId, assignedTemplateIds, onClose }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ["assignable-habit-templates"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_template")
        .select("id, name")
        .eq("archived", false)
        .order("name", { ascending: true })
        .returns<TemplateOptionRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const options = useMemo(() => {
    const assigned = new Set(assignedTemplateIds);
    return (templatesQuery.data ?? [])
      .filter((template) => !assigned.has(template.id))
      .map((template) => ({ value: template.id, label: template.name }));
  }, [templatesQuery.data, assignedTemplateIds]);

  const assignMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const row = {
        templateId,
        traineeId,
        userId,
        createdBy: memberId ?? null,
        status: HabitStatus.Active,
        startDate: dayjs().format("YYYY-MM-DD"),
      };

      const { error } = await supabase
        .from("client_habit")
        .upsert(row as never, { onConflict: "templateId,traineeId", ignoreDuplicates: true });

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Przypisano");
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.habits(traineeId) });
      setSelectedTemplateId(null);
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się przypisać: ${error.message}`);
    },
  });

  return (
    <Modal
      title={<Typography.Text>Przypisz nawyk / zadanie</Typography.Text>}
      open={open}
      onCancel={() => {
        setSelectedTemplateId(null);
        onClose();
      }}
      onOk={() => selectedTemplateId && assignMutation.mutate(selectedTemplateId)}
      okText="Przypisz"
      cancelText="Anuluj"
      confirmLoading={assignMutation.isLoading}
      okButtonProps={{ disabled: !selectedTemplateId }}
      destroyOnClose
    >
      {options.length === 0 && !templatesQuery.isFetching ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak nawyków i zadań do przypisania" />
      ) : (
        <Select
          style={{ width: "100%" }}
          placeholder="Wybierz nawyk lub zadanie"
          loading={templatesQuery.isFetching}
          showSearch
          optionFilterProp="label"
          value={selectedTemplateId}
          onChange={setSelectedTemplateId}
          options={options}
        />
      )}
    </Modal>
  );
};

export default AssignHabitModal;
